import React from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {toast} from 'react-toastify';

import {downloadMarkingCodesByTINS} from 'api';
import {DropdownMenu} from 'components//ui-kit/DropdownMenu';
import {ProductGroupIcon} from 'components/common/ProductGroupIcon';
import {ButtonVariant} from 'components/ui-kit/Button';
import {EndlessList} from 'components/ui-kit/EndlessList';
import {Table} from 'components/ui-kit/Table';
import {commonDataState$, fetchLabelsEvent, Gate, productsTableState$} from 'models/orders/detail';
import {breakpoint} from 'theme';
import {PackageTypes, TRANSPORT_PACKAGE_TYPES} from 'types/gtins';
import {
    FormattedLabelsForm,
    OrderDetailProductsFormattedRow,
    OrderDetailProductsRaw,
    OrdersDetailProductsAssessors,
} from 'types/orders';
import {useMatchMedia} from 'utils/custom-hooks';
import {handleNil} from 'utils/handleNil';

import {PrintModal} from '../PrintModal';

import {FlexRowElement, ListRow, Quantity, Text} from './styled';

export function convertData(
    data: OrderDetailProductsRaw[] | undefined,
): OrderDetailProductsFormattedRow[] {
    if (!data) return [];

    return data.map((elem) => ({
        [OrdersDetailProductsAssessors.productId]: handleNil(elem?.productId),
        [OrdersDetailProductsAssessors.productName]: handleNil(elem?.productName),
        [OrdersDetailProductsAssessors.quantity]: handleNil(elem?.quantity, (value) => (
            <Quantity>{value}</Quantity>
        )),
        [OrdersDetailProductsAssessors.packageType]: handleNil(elem?.packageType),
        [OrdersDetailProductsAssessors.productGroupCode]: [
            ...TRANSPORT_PACKAGE_TYPES,
            PackageTypes.CUSTOMS_UNIT,
        ].includes(elem.packageType)
            ? '—'
            : handleNil(elem.productGroupCode, (value) => <ProductGroupIcon value={value} label />),
        [OrdersDetailProductsAssessors.availableQuantity]: handleNil(elem?.availableQuantity),
        [OrdersDetailProductsAssessors.gtin]: handleNil(elem?.gtin),
        [OrdersDetailProductsAssessors.printed]: elem.printed,
    }));
}

type BasePrintData = {
    gtin: string;
    format: FormattedLabelsForm['format'];
    packageType?: PackageTypes;
    specificFormat?: boolean;
};

export const Products = () => {
    const {id} = useStore(Gate.state);

    const [isPrintModalOpen, setIsPrintModalOpen] = React.useState(false);
    const [basePrintData, setBasePrintData] = React.useState<BasePrintData | null>(null);

    const onSelectExtension = (data: BasePrintData) => {
        data.specificFormat =
            data.packageType &&
            (TRANSPORT_PACKAGE_TYPES.includes(data.packageType) ||
                data.packageType === 'CUSTOMS_UNIT');
        setBasePrintData(data);
        setIsPrintModalOpen(true);
    };

    const onConfirmPrint = (pageFormat: FormattedLabelsForm['pageFormat']) => {
        if (basePrintData) {
            fetchLabelsEvent({
                orderId: id,
                gtin: basePrintData.gtin,
                format: basePrintData.format,
                pageFormat,
                showHri: false,
            });
        }
        setIsPrintModalOpen(false);
    };

    const onDownload = async (gtin: string, format = 'csv') => {
        try {
            if (!gtin) throw Error('GTIN is required');
            await downloadMarkingCodesByTINS({gtin, orderId: id, format});
        } catch (err) {
            console.error('Error:', err);
            toast.error(err);
        }
    };

    const getConfig = (t: TFunction, status?: string) => [
        {
            Header: t('pages.orders.productGroup'),
            accessor: OrdersDetailProductsAssessors.productGroupCode,
            width: 150,
            disableSortBy: true,
        },
        {
            Header: t('pages.orders.form.type'),
            accessor: OrdersDetailProductsAssessors.packageType,
            width: 150,
            disableSortBy: true,
        },
        {
            Header: t('pages.orders.detail.productsTab.productName'),
            accessor: OrdersDetailProductsAssessors.productName,
            width: 170,
            disableSortBy: true,
        },
        {
            Header: t('pages.orders.detail.productsTab.quantity'),
            accessor: OrdersDetailProductsAssessors.quantity,
            width: 150,
            disableSortBy: true,
        },
        {
            Header: t('pages.orders.detail.productsTab.gtin'),
            accessor: OrdersDetailProductsAssessors.gtin,
            width: 150,
            disableSortBy: true,
        },
        {
            Header: 'Action',
            accessor: 'action-print',
            Cell: ({row}: {row: {original: OrderDetailProductsFormattedRow}}) => (
                <>
                    {
                        <DropdownMenu
                            label="Print"
                            width={109}
                            buttonVariant={ButtonVariant.Secondary}
                            buttonSize="sm"
                        >
                            <DropdownMenu.List>
                                {status !== 'DECLINED' && !row.original.printed && (
                                    <>
                                        <DropdownMenu.Item
                                            onSelect={() =>
                                                onSelectExtension({
                                                    gtin: row.original.gtin,
                                                    format: 'PDF',
                                                    packageType: row.original.packageType,
                                                })
                                            }
                                        >
                                            PDF
                                        </DropdownMenu.Item>
                                        <DropdownMenu.Item
                                            onSelect={() =>
                                                onSelectExtension({
                                                    gtin: row.original.gtin,
                                                    format: 'EPS',
                                                    packageType: row.original.packageType,
                                                })
                                            }
                                        >
                                            EPS
                                        </DropdownMenu.Item>
                                    </>
                                )}
                                <DropdownMenu.Item
                                    onSelect={() => onDownload(row.original.gtin, 'XLS')}
                                >
                                    XLS
                                </DropdownMenu.Item>
                                <DropdownMenu.Item
                                    onSelect={() => onDownload(row.original.gtin, 'CSV')}
                                >
                                    CSV
                                </DropdownMenu.Item>
                            </DropdownMenu.List>
                        </DropdownMenu>
                    }
                </>
            ),
            disableSortBy: true,
        },
    ];

    const {t} = useTranslation();
    const {data, isLoading} = useStore(productsTableState$);
    const {data: commonData} = useStore(commonDataState$);
    const status = commonData?.status;
    const columns = React.useMemo(() => getConfig(t, status), [t, status]);
    const isMobile = useMatchMedia(`(max-width: ${breakpoint.phone})`);

    const mappedData = React.useMemo(() => convertData(data), [data, t]);

    return (
        <div>
            {!isMobile && (
                <Table
                    columns={columns}
                    data={mappedData}
                    totalRowsCount={data.length}
                    isLoading={isLoading}
                    hideSetting
                />
            )}

            {isMobile && (
                <EndlessList<OrderDetailProductsFormattedRow>
                    rows={mappedData}
                    totalRowsCount={data.length}
                    renderRow={(row: OrderDetailProductsFormattedRow) => (
                        <ListRow>
                            <FlexRowElement>
                                <Text isGray>{row[OrdersDetailProductsAssessors.productName]}</Text>
                            </FlexRowElement>
                            <FlexRowElement>
                                <Text>{row[OrdersDetailProductsAssessors.productId]}</Text>
                            </FlexRowElement>
                            <FlexRowElement>
                                <Text isGray>{row[OrdersDetailProductsAssessors.gtin]}</Text>
                                <Text isGray>{row[OrdersDetailProductsAssessors.quantity]}</Text>
                            </FlexRowElement>
                        </ListRow>
                    )}
                    config={columns}
                />
            )}
            <PrintModal
                show={isPrintModalOpen}
                onClose={() => setIsPrintModalOpen(false)}
                onConfirm={onConfirmPrint}
                specificFormat={basePrintData?.specificFormat}
            />
        </div>
    );
};
